/**
 * Shared constants and utilities for financial reports across admin and landing pages
 */

// Format currency values
export const formatCurrency = (value) => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }
  const num = Number(value);
  if (Number.isNaN(num)) {
    return value;
  }
  return num.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

// Format percentage values
export const formatPercentage = (value) => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }
  const num = Number(value);
  if (Number.isNaN(num)) {
    return value;
  }
  return `${(num * 100).toFixed(2)}%`;
};

// Build metric rows from config
export const buildMetricRows = (record, config) =>
  config.map(({ key, label, formatter }) => ({
    key,
    item: label,
    value: formatter
      ? formatter(record?.[key])
      : formatCurrency(record?.[key]),
  }));

// Helper function to normalize basicIndustry
export const normalizeBasicIndustry = (basicIndustry) => {
  if (!basicIndustry) return 'manufacturing';
  const industryLower = String(basicIndustry).toLowerCase().trim();
  
  // Bank industries
  if (industryLower.includes('public sector bank') || 
      industryLower.includes('private sector bank') || 
      industryLower.includes('public sector banks') || 
      industryLower.includes('private sector banks')) {
    return 'bank';
  }
  
  // NBFC industries
  if (industryLower.includes('nbfc') || 
      industryLower.includes('non-banking financial company') ||
      industryLower.includes('housing finance company')) {
    return 'nbfc';
  }
  
  // Default to manufacturing
  return 'manufacturing';
};

// Metric table columns configuration
export const METRIC_COLUMNS = [
  {
    title: "Metric",
    dataIndex: "item",
    key: "item",
  },
  {
    title: "Value",
    dataIndex: "value",
    key: "value",
    align: "right",
  },
];

// Bank Income Fields
export const BANK_INCOME_FIELDS = [
  { key: "totalIncome", label: "Total Income" },
  { key: "revenueFromOperations", label: "Revenue from Operations" },
  { key: "otherIncome", label: "Other Income" },
  { key: "totalExpenses", label: "Total Expenses" },
  { key: "financeCosts", label: "Finance Costs" },
  { key: "depreciationDepletionAndAmortisationExpense", label: "Depreciation & Amortisation" },
  { key: "otherExpenses", label: "Other Expenses" },
  { key: "operatingProfit", label: "Operating Profit" },
  { key: "provisionsAndContingencies", label: "Provisions and Contingencies" },
  { key: "provisionsForNpa", label: "Provisions for NPA" },
  { key: "profitLossBeforeTax", label: "Profit Before Tax" },
  { key: "profitLossForPeriod", label: "Profit After Tax" },
  { key: "totalComprehensiveIncome", label: "Total Comprehensive Income" },
];

// Bank Asset Fields
export const BANK_ASSET_FIELDS = [
  { key: "assets", label: "Total Assets" },
  { key: "investments", label: "Investments" },
  { key: "advances", label: "Advances" },
  { key: "otherAssets", label: "Other Assets" },
];

// Bank Liability Fields
export const BANK_LIABILITY_FIELDS = [
  { key: "liabilities", label: "Total Liabilities" },
  { key: "equity", label: "Equity" },
  { key: "capital", label: "Capital" },
  { key: "reserves", label: "Reserves" },
  { key: "reservesExcludingRevaluation", label: "Reserves Excluding Revaluation" },
  { key: "deposits", label: "Deposits" },
  { key: "borrowings", label: "Borrowings" },
];

// Bank Key Metrics
export const BANK_KEY_METRIC_FIELDS = [
  { key: "netWorth", label: "Net Worth" },
  { key: "capitalAdequacyRatio", label: "Capital Adequacy Ratio", formatter: formatPercentage },
  { key: "cet1Ratio", label: "CET1 Ratio", formatter: formatPercentage },
  { key: "additionalTier1Ratio", label: "Additional Tier 1 Ratio", formatter: formatPercentage },
  { key: "grossNpaRatio", label: "Gross NPA Ratio", formatter: formatPercentage },
  { key: "netNpaRatio", label: "Net NPA Ratio", formatter: formatPercentage },
  { key: "returnOnAssets", label: "Return on Assets", formatter: formatPercentage },
  { key: "debtEquityRatio", label: "Debt Equity Ratio" },
  { key: "totalDebtsToTotalAssets", label: "Total Debts to Total Assets" },
];

// NBFC Income Fields
export const NBFC_INCOME_FIELDS = [
  { key: "totalIncome", label: "Total Income" },
  { key: "revenueFromOperations", label: "Revenue from Operations" },
  { key: "interestIncome", label: "Interest Income" },
  { key: "feesAndCommissionIncome", label: "Fees and Commission Income" },
  { key: "otherIncome", label: "Other Income" },
  { key: "totalExpenses", label: "Total Expenses" },
  { key: "financeCosts", label: "Finance Costs" },
  { key: "depreciationDepletionAndAmortisationExpense", label: "Depreciation & Amortisation" },
  { key: "otherExpenses", label: "Other Expenses" },
  { key: "impairmentOnFinancialInstruments", label: "Impairment on Financial Instruments" },
  { key: "employeeBenefitExpenses", label: "Employee Benefit Expenses" },
  { key: "profitLossBeforeTax", label: "Profit Before Tax" },
  { key: "taxExpenseTotal", label: "Tax Expense" },
  { key: "profitLossForPeriod", label: "Profit After Tax" },
  { key: "netProfitLossAfterTax", label: "Net Profit After Tax" },
  { key: "totalComprehensiveIncome", label: "Total Comprehensive Income" },
];

// NBFC Asset Fields
export const NBFC_ASSET_FIELDS = [
  { key: "assets", label: "Total Assets" },
  { key: "financialAssets", label: "Financial Assets" },
  { key: "nonFinancialAssets", label: "Non-Financial Assets" },
];

// NBFC Liability Fields
export const NBFC_LIABILITY_FIELDS = [
  { key: "liabilities", label: "Total Liabilities" },
  { key: "equity", label: "Equity" },
  { key: "financialLiabilities", label: "Financial Liabilities" },
  { key: "nonFinancialLiabilities", label: "Non-Financial Liabilities" },
];

// NBFC Key Metrics
export const NBFC_KEY_METRIC_FIELDS = [
  { key: "netWorth", label: "Net Worth" },
  { key: "basicEarningsPerShare", label: "Basic Earnings Per Share" },
  { key: "dilutedEarningsPerShare", label: "Diluted Earnings Per Share" },
  { key: "earningsPerShareBasic", label: "Earnings Per Share Basic" },
  { key: "earningsPerShareDiluted", label: "Earnings Per Share Diluted" },
  { key: "debtEquityRatio", label: "Debt Equity Ratio" },
  { key: "totalDebtsToTotalAssets", label: "Total Debts to Total Assets" },
  { key: "debtServiceCoverageRatio", label: "Debt Service Coverage Ratio" },
  { key: "interestServiceCoverageRatio", label: "Interest Service Coverage Ratio" },
  { key: "currentRatio", label: "Current Ratio" },
  { key: "operatingMargin", label: "Operating Margin", formatter: formatPercentage },
  { key: "grossNpaRatio", label: "Gross NPA Ratio", formatter: formatPercentage },
  { key: "netNpaRatio", label: "Net NPA Ratio", formatter: formatPercentage },
  { key: "capitalAdequacyRatio", label: "Capital Adequacy Ratio", formatter: formatPercentage },
  { key: "debentureRedemptionReserve", label: "Debenture Redemption Reserve" },
];

// Manufacturing Income Fields
export const MANUFACTURING_INCOME_FIELDS = [
  { key: "totalIncome", label: "Total Income" },
  { key: "revenueFromOperations", label: "Revenue from Operations" },
  { key: "otherIncome", label: "Other Income" },
  { key: "totalExpenses", label: "Total Expenses" },
  { key: "financeCosts", label: "Finance Costs" },
  { key: "depreciationDepletionAndAmortisationExpense", label: "Depreciation & Amortisation" },
  { key: "otherExpenses", label: "Other Expenses" },
  { key: "profitLossBeforeTax", label: "Profit Before Tax" },
  { key: "profitLossForPeriod", label: "Profit After Tax" },
  { key: "totalComprehensiveIncome", label: "Total Comprehensive Income" },
];

// Manufacturing Asset Fields (Annual only)
export const MANUFACTURING_ASSET_FIELDS = [
  { key: "assets", label: "Total Assets" },
  { key: "nonCurrentAssets", label: "Non-Current Assets" },
  { key: "currentAssets", label: "Current Assets" },
];

// Manufacturing Liability Fields (Annual only)
export const MANUFACTURING_LIABILITY_FIELDS = [
  { key: "liabilities", label: "Total Liabilities" },
  { key: "equity", label: "Equity" },
  { key: "nonCurrentLiabilities", label: "Non-Current Liabilities" },
  { key: "currentLiabilities", label: "Current Liabilities" },
  { key: "deferredRevenue", label: "Deferred Revenue" },
  { key: "capital", label: "Capital" },
  { key: "borrowings", label: "Borrowings" },
];

// Manufacturing Key Metrics
export const MANUFACTURING_KEY_METRIC_FIELDS = [
  { key: "netWorth", label: "Net Worth" },
  { key: "debentureRedemptionReserve", label: "Debenture Redemption Reserve" },
  { key: "debtServiceCoverageRatio", label: "Debt Service Coverage Ratio" },
  { key: "interestServiceCoverageRatio", label: "Interest Service Coverage Ratio" },
  { key: "debtEquityRatio", label: "Debt Equity Ratio" },
  { key: "currentRatio", label: "Current Ratio" },
  { key: "longTermDebtToWorkingCapital", label: "Long Term Debt to Working Capital" },
  { key: "badDebtsToAccountReceivables", label: "Bad Debts to Account Receivables" },
  { key: "currentLiabilityRatio", label: "Current Liability Ratio" },
  { key: "totalDebtsToTotalAssets", label: "Total Debts to Total Assets" },
  { key: "debtorTurnoverRatio", label: "Debtor Turnover Ratio" },
  { key: "inventoryTurnoverRatio", label: "Inventory Turnover Ratio" },
  { key: "operatingMargin", label: "Operating Margin", formatter: formatPercentage },
  { key: "netProfitMargin", label: "Net Profit Margin", formatter: formatPercentage },
  { key: "basicEarningsPerShare", label: "Basic Earnings Per Share" },
];

// Common Cash Flow Fields (for Bank, NBFC, and Manufacturing Annual)
export const CASH_FLOW_FIELDS = [
  { key: "cashFlowsFromUsedInOperatingActivities", label: "Operating Activities" },
  { key: "cashFlowsFromUsedInInvestingActivities", label: "Investing Activities" },
  { key: "cashFlowsFromUsedInFinancingActivities", label: "Financing Activities" },
  { key: "increaseDecreaseInCashAndCashEquivalents", label: "Net Change in Cash" },
  { key: "cashAndCashEquivalentsCashFlowStatement", label: "Cash & Cash Equivalents" },
];

// Function to get fields based on industry and period type
export const getFieldsByIndustry = (basicIndustry, periodType) => {
  const industry = normalizeBasicIndustry(basicIndustry);
  const isQuarterly = periodType === 'quarterly' || periodType?.toLowerCase().includes('quarter');
  
  if (industry === 'bank') {
    return {
      income: BANK_INCOME_FIELDS,
      assets: BANK_ASSET_FIELDS,
      liabilities: BANK_LIABILITY_FIELDS,
      cashFlow: CASH_FLOW_FIELDS,
      keyMetrics: BANK_KEY_METRIC_FIELDS,
    };
  } else if (industry === 'nbfc') {
    return {
      income: NBFC_INCOME_FIELDS,
      assets: NBFC_ASSET_FIELDS,
      liabilities: NBFC_LIABILITY_FIELDS,
      cashFlow: CASH_FLOW_FIELDS,
      keyMetrics: NBFC_KEY_METRIC_FIELDS,
    };
  } else {
    // Manufacturing
    if (isQuarterly) {
      // Quarterly: No Balance Sheet or Cash Flow
      return {
        income: MANUFACTURING_INCOME_FIELDS,
        assets: [],
        liabilities: [],
        cashFlow: [],
        keyMetrics: MANUFACTURING_KEY_METRIC_FIELDS,
      };
    } else {
      // Annual: All fields
      return {
        income: MANUFACTURING_INCOME_FIELDS,
        assets: MANUFACTURING_ASSET_FIELDS,
        liabilities: MANUFACTURING_LIABILITY_FIELDS,
        cashFlow: CASH_FLOW_FIELDS,
        keyMetrics: MANUFACTURING_KEY_METRIC_FIELDS,
      };
    }
  }
};

/** Parse end-date for sorting financial reports (newest first). */
export const financialReportEndTs = (r) => {
  const s = r?.endDate || r?.periodEnd;
  if (!s) return 0;
  const t = Date.parse(s);
  return Number.isNaN(t) ? 0 : t;
};

/** Copy & sort issuer financial periods by reporting end date descending. */
export const sortIssuerFinancialReportsDesc = (reports) =>
  [...(reports || [])].sort((a, b) => financialReportEndTs(b) - financialReportEndTs(a));

/** 'annual' | 'quarterly' from filing `periodType`. */
export const getFinancialReportPeriodKind = (report) => {
  const pt = String(report?.periodType || '').toLowerCase();
  if (pt.includes('quarter')) return 'quarterly';
  if (pt.includes('year') || pt.includes('annual')) return 'annual';
  return 'annual';
};

/**
 * True when two filings match the same period line (distinct annual vs quarter
 * can share the same calendar end date).
 */
export const financialReportsAppearEqual = (a, b) => {
  if (!a || !b) return false;
  if ((a.periodType || '') !== (b.periodType || '')) return false;
  const e1 = a.endDate || a.periodEnd;
  const e2 = b.endDate || b.periodEnd;
  if (e1 !== e2) return false;
  if ((a.columnUsed || '') !== (b.columnUsed || '')) return false;
  return (a.id?.statementType || '') === (b.id?.statementType || '');
};

